// Catálogo de «Códigos y cupones»: sub-vistas, sus textos, estados posibles y tono y orden de cada estado.
import type { PillTone } from '@/shared/patterns/Pills';
import type { CodeListSort, CodeStatus, CodeView } from '../lib/discountCodesList';

export const DEFAULT_CODE_VIEW: CodeView = 'ADMIN';

export const CODE_VIEW_OPTIONS: readonly { value: CodeView; label: string }[] = [
    { value: 'ADMIN', label: 'Códigos de descuento' },
    { value: 'SALE_COUPON', label: 'Cupones emitidos' },
];

export interface CodeViewCopy {
    one: string;
    many: string;
    empty: string;
    /** Vacío cuando todavía hay páginas sin cargar. */
    emptyLoaded: string;
    loadError: string;
    caption: string;
    filterRetailer: string;
}

/** Cómo se nombran las filas, el vacío, la falla y el filtro de cadena de cada sub-vista. */
export const CODE_VIEW_COPY: Record<CodeView, CodeViewCopy> = {
    ADMIN: {
        one: 'código',
        many: 'códigos',
        empty: 'No hay códigos de descuento con estos filtros.',
        emptyLoaded:
            'No hay códigos de descuento con estos filtros entre los cargados. Usa «Cargar más» para buscar en el resto.',
        loadError: 'No pudimos cargar los códigos de descuento',
        caption: 'Códigos de descuento con su estado, vigencia y usos.',
        filterRetailer: 'Muestra solo los códigos de esa cadena.',
    },
    SALE_COUPON: {
        one: 'cupón',
        many: 'cupones',
        empty: 'No hay cupones emitidos con estos filtros.',
        emptyLoaded:
            'No hay cupones emitidos con estos filtros entre los cargados. Usa «Cargar más» para buscar en el resto.',
        loadError: 'No pudimos cargar los cupones emitidos',
        caption: 'Cupones emitidos por las ventas con su estado, vigencia y usos.',
        filterRetailer: 'Muestra solo los cupones de esa cadena.',
    },
};

/** Aviso sobre la tabla cuando faltan páginas: búsqueda, orden y conteo son de lo cargado. */
export function codesLoadedNotice(
    loaded: number,
    copy: Pick<CodeViewCopy, 'one' | 'many'>,
): string {
    const what =
        loaded === 1 ? `el ${copy.one} cargado` : `los ${String(loaded)} ${copy.many} cargados`;
    return `Buscas y ordenas entre ${what}; usa «Cargar más» para ver el resto.`;
}

export const ALL_CODE_STATUSES = 'ALL';

/** Los estados que puede tener cada sub-vista: un código no se «usa», un cupón no se «agota». */
export const CODE_VIEW_STATUSES: Record<CodeView, readonly CodeStatus[]> = {
    ADMIN: ['ACTIVE', 'EXHAUSTED', 'EXPIRED', 'VOIDED'],
    SALE_COUPON: ['ACTIVE', 'USED', 'EXPIRED', 'VOIDED'],
};

/** Tono común del portal para cada estado de código o cupón. */
export const CODE_STATUS_TONE: Record<CodeStatus, PillTone> = {
    ACTIVE: 'success',
    USED: 'info',
    EXHAUSTED: 'warning',
    EXPIRED: 'neutral',
    VOIDED: 'neutral',
};

/** Orden por estado: lo vigente primero, lo anulado al final. */
export const CODE_STATUS_RANK: Record<CodeStatus, number> = {
    ACTIVE: 0,
    EXHAUSTED: 1,
    USED: 2,
    EXPIRED: 3,
    VOIDED: 4,
};

/** Alfabética por el texto del código, la convención de las listas del portal. */
export const DEFAULT_CODE_SORT: CodeListSort = { key: 'code', direction: 'asc' };
